import { useState, useRef, useEffect, useCallback } from "react";
import { marked } from "marked";
import "./AgentPage.css";
import { agentHealth } from "./api";

/**
 * AgentPage - Job Matching Agent
 *
 * Chat interface ported from Minerva_intelligence_UI_prototype 1.html's
 * #view-agent. The prototype was fully mocked; this version proxies each
 * message to IRIS's dedicated Minerva API via New-Minerva's Flask
 * (POST /api/agent/message, Server-Sent Events) and renders the streamed
 * reply live. Matching uses only the internal Minerva catalog; this page relays and
 * displays.
 *
 * SSE frames from the Minerva API (passed straight through by the proxy):
 *   {type:"delta", text}                    incremental markdown
 *   {type:"tool_status", tool, input}       agent called a retrieval tool
 *   {type:"tool_result", tool, data}        structured internal matches
 *   {type:"done", chat_id, reply, matches, finalized_job_id}
 *   {type:"error", message}
 */

const STARTERS = [
  { label: "Match an engineering role", q: "engineer developing LLM-powered solutions" },
  {
    label: "Match a finance role",
    q: "Supports the finance manager with month-end close, budgeting and reporting; supervises junior finance staff",
  },
  { label: "Explain a job code", q: "What does the F-CORE-4 code cover?" },
  { label: "How tiers work", q: "How do experience tiers work?" },
];

const SOURCE_LABELS = {
  Minerva: "Potentiam catalog",
};

const NOT_CONFIGURED = "Agent is not configured";

async function checkAgentResponse(res) {
  if (res.ok) return;
  const data = res.status === 503 ? await res.json().catch(() => null) : null;
  throw new Error(data?.error === NOT_CONFIGURED ? NOT_CONFIGURED : `HTTP ${res.status}`);
}

marked.setOptions({ breaks: true, gfm: true });

// Minimal allow-list sanitizer for the markdown IRIS/Claude produces. Parses
// into a detached template, drops any element not on the list (keeping its
// text), and strips every attribute except href on <a> (forced safe).
const ALLOWED_TAGS = new Set(["P", "BR", "STRONG", "EM", "B", "I", "CODE", "PRE", "UL", "OL", "LI", "A", "H3", "H4", "BLOCKQUOTE", "SPAN"]);
function sanitize(html) {
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  const walk = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === 1) {
        if (!ALLOWED_TAGS.has(child.tagName)) {
          const text = document.createTextNode(child.textContent || "");
          child.replaceWith(text);
          return;
        }
        [...child.attributes].forEach((attr) => {
          if (child.tagName === "A" && attr.name === "href" && /^https?:\/\//i.test(attr.value)) return;
          child.removeAttribute(attr.name);
        });
        if (child.tagName === "A") {
          child.setAttribute("target", "_blank");
          child.setAttribute("rel", "noopener noreferrer");
        }
        walk(child);
      } else if (child.nodeType !== 3) {
        child.remove();
      }
    });
  };
  walk(tpl.content);
  return tpl.innerHTML;
}

// Pull an optional [[QUICK_REPLIES]] a | b | c [[/QUICK_REPLIES]] block out of
// the accumulated text (IRIS convention - see its chat.html). Returns the text
// with the block removed and the parsed reply list.
function extractQuickReplies(text) {
  const m = text.match(/\[\[QUICK_REPLIES\]\]([\s\S]*?)\[\[\/QUICK_REPLIES\]\]/);
  if (!m) return { text, quickReplies: [] };
  const quickReplies = m[1]
    .split(/\||\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  return { text: text.replace(m[0], "").trim(), quickReplies };
}

// Normalise any tool_result payload into a flat list of match rows.
export function extractMatches(evt) {
  const d = evt.data;
  if (!d) return { matches: [], outOfScope: false };
  if (d.out_of_scope) return { matches: [], outOfScope: true };
  let rows = [];
  if (Array.isArray(d)) rows = d;
  else if (Array.isArray(d.results)) rows = d.results;
  else if (Array.isArray(d.matches)) rows = d.matches;
  else if (Array.isArray(d.candidates)) rows = d.candidates;
  if (d.closest_internal && !rows.some((r) => r.job_code === d.closest_internal.job_code)) {
    rows = [d.closest_internal, ...rows];
  }
  const matches = rows
    .filter((r) => r && r.job_code)
    .map((r) => ({
      id: r.id,
      code: r.job_code,
      title: r.job_title || r.title || r.job_code,
      source: "Minerva",
      fitPct: null,
      why: [r.substack, r.vertical_stack].filter(Boolean).join(" · ") ||
        (r.job_description ? String(r.job_description).slice(0, 140) : ""),
      final: r.id != null && r.id === d.finalized_job_id,
    }));
  return { matches, outOfScope: false };
}

export function restoreTranscript(chat) {
  let quickReplies = [];
  const messages = (Array.isArray(chat.transcript) ? chat.transcript : [])
    .filter((msg) => msg && ["user", "assistant"].includes(msg.role) && typeof msg.content === "string")
    .map((msg) => {
      if (msg.role === "user") return { role: "user", text: msg.content, matches: [] };
      const parsed = extractQuickReplies(msg.content);
      quickReplies = parsed.quickReplies;
      return { role: "bot", text: parsed.text, matches: [] };
    });
  const lastBot = messages.map((msg) => msg.role).lastIndexOf("bot");
  if (lastBot !== -1) {
    messages[lastBot].matches = visibleMatches(extractMatches({ tool: "search_internal_jobs", data: chat }).matches);
  }
  return { messages, quickReplies };
}

export function visibleMatches(list, limit = 6) {
  const byCode = new Map();
  list.forEach((m) => {
    if (m.final === true || !byCode.has(m.code)) byCode.set(m.code, m);
  });
  const ranked = [...byCode.values()];
  const visible = ranked.slice(0, limit);
  const final = ranked.find((m) => m.final === true);
  if (final && visible.length && !visible.includes(final)) visible[visible.length - 1] = final;
  return visible;
}

function mergeMatches(existing, incoming) {
  return visibleMatches([...existing, ...incoming]);
}

export function splitSseFrames(buffer, flush = false) {
  const events = buffer.replace(/\r\n/g, "\n").split("\n\n");
  const rest = events.pop() || "";
  if (flush && rest.trim()) events.push(rest);
  return { events, rest: flush ? "" : rest };
}

function RobotIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <rect x="4" y="8" width="16" height="12" rx="3" />
      <path d="M12 8V4" />
      <circle cx="12" cy="3" r="1.4" />
      <circle cx="9" cy="14" r="1.1" />
      <circle cx="15" cy="14" r="1.1" />
    </svg>
  );
}

function MatchCard({ m, rank }) {
  return (
    <div className={`ag-match${m.final ? " ag-match-final" : ""}`}>
      <div className="ag-match-top">
        <span className={`ag-match-rank${m.final ? " ag-star" : ""}`}>{m.final ? "★" : rank}</span>
        <h4>{m.title}</h4>
        {m.code && <span className="ag-code-chip">{m.code}</span>}
        {m.fitPct != null && (
          <span className="ag-fit">
            <span className="ag-bar">
              <span style={{ width: `${m.fitPct}%` }} />
            </span>
            {m.fitPct}%
          </span>
        )}
      </div>
      {m.source && <p style={{ fontSize: 12, color: "var(--ag-ink-mute)", margin: "0 0 2px" }}>{SOURCE_LABELS[m.source] || m.source}</p>}
      {m.why && (
        <div className="ag-why">
          <b>{m.final ? "Recommended" : "Why this fits"}:</b> {m.why}
        </div>
      )}
    </div>
  );
}

function BotBubble({ msg }) {
  const showTyping = msg.streaming && !msg.text;
  return (
    <div className="ag-msg bot">
      <div className="ag-av">M</div>
      <div style={{ maxWidth: "88%" }}>
        <div className={`ag-bubble${msg.error ? " ag-error" : ""}`}>
          {showTyping ? (
            <div className="ag-typing">
              <i />
              <i />
              <i />
            </div>
          ) : msg.seed ? (
            msg.seedContent
          ) : (
            <span dangerouslySetInnerHTML={{ __html: sanitize(marked.parse(msg.text || "")) }} />
          )}
        </div>
        {msg.toolStatus && <div className="ag-tool-status">{msg.toolStatus}</div>}
        {msg.matches && msg.matches.length > 0 && (
          <div>
            {msg.matches.map((m, i) => (
              <MatchCard key={m.code || i} m={m} rank={i + 1} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const TOOL_LABELS = {
  search_internal_jobs: "Searching the Potentiam job catalog…",
  get_internal_job: "Looking up the Potentiam role…",
  get_catalog_info: "Checking the Potentiam catalog…",
  finalize_internal_match: "Finalising the recommendation…",
  assess_internal_level: "Checking the level ladder…",
};

export default function AgentPage() {
  const seedMessage = {
    role: "bot",
    seed: true,
    seedContent: (
      <>
        Hello &mdash; I&apos;m the Minerva Job Matching Agent. I&apos;ll help you map your roles to the right internal Potentiam job code (the 446-role catalog). Here&apos;s how I can help:
        <ul>
          <li>
            <strong>Match job descriptions</strong> to internal Potentiam job codes
          </li>
          <li>
            <strong>Explain the matching rationale</strong> behind each recommendation
          </li>
          <li>
            <strong>Compare close matches</strong> so you can pick the best fit
          </li>
          <li>
            <strong>Answer questions</strong> about the Potentiam job architecture
          </li>
        </ul>
        Pick a starting point below, or just tell me about the role you&apos;d like to match.
      </>
    ),
  };

  const [messages, setMessages] = useState([seedMessage]);
  const [sessionId, setSessionId] = useState(null);
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [offline, setOffline] = useState(false);
  const [unconfigured, setUnconfigured] = useState(false);
  const [starterChips, setStarterChips] = useState(STARTERS.map((s) => s.label));
  const [history, setHistory] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [historyNotice, setHistoryNotice] = useState("");
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyBusy, setHistoryBusy] = useState(false);
  const busy = streaming || historyBusy || historyLoading || unconfigured;

  const chatRef = useRef(null);
  const taRef = useRef(null);
  const requestRef = useRef(null);
  const historyRequestRef = useRef(null);

  const handleHistoryError = useCallback((err, notice) => {
    if (err.name === "AbortError") return;
    if (err.message === NOT_CONFIGURED) setUnconfigured(true);
    else setHistoryNotice(notice);
  }, []);

  const refreshHistory = useCallback(async () => {
    const res = await fetch("/api/agent/chats", {
      credentials: "same-origin", signal: historyRequestRef.current?.signal,
    });
    await checkAgentResponse(res);
    const data = await res.json();
    if (!Array.isArray(data.chats)) throw new Error("Invalid chat history");
    setHistory(data.chats.map((chat) => ({
      id: chat.chat_id, title: chat.title, createdAt: chat.created_at, updatedAt: chat.updated_at,
    })));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    historyRequestRef.current = controller;
    refreshHistory()
      .catch((err) => {
        if (!controller.signal.aborted) handleHistoryError(err, "History could not be loaded. Please reload to try again.");
      })
      .finally(() => { if (!controller.signal.aborted) setHistoryLoading(false); });
    return () => controller.abort();
  }, [refreshHistory, handleHistoryError]);

  useEffect(() => () => requestRef.current?.abort(), []);

  useEffect(() => {
    let cancelled = false;
    agentHealth()
      .then((h) => !cancelled && setOffline(!h || h.status === "down"))
      .catch((err) => {
        if (cancelled) return;
        setOffline(true);
        if (err.message === NOT_CONFIGURED) setUnconfigured(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  const autosize = useCallback(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
  }, []);

  const newChat = () => {
    if (busy) return;
    setMessages([seedMessage]);
    setSessionId(null);
    setConversationId(null);
    setDraft("");
    setStarterChips(STARTERS.map((s) => s.label));
    requestAnimationFrame(autosize);
  };

  const reopenChat = async (conversation) => {
    if (busy) return;
    setHistoryBusy(true);
    try {
      const res = await fetch(`/api/agent/chats/${encodeURIComponent(conversation.id)}`, {
        credentials: "same-origin", signal: historyRequestRef.current?.signal,
      });
      await checkAgentResponse(res);
      const chat = await res.json();
      const restored = restoreTranscript(chat);
      setMessages(restored.messages);
      setStarterChips(restored.quickReplies.length ? restored.quickReplies : STARTERS.map((s) => s.label));
      setSessionId(chat.chat_id);
      setHistoryNotice("");
    } catch (err) {
      handleHistoryError(err, "This conversation could not be opened. Please try again.");
      return;
    } finally {
      setHistoryBusy(false);
    }
    setConversationId(conversation.id);
    setDraft("");
    requestAnimationFrame(autosize);
  };

  const deleteChat = async (id) => {
    if (busy) return;
    setHistoryBusy(true);
    try {
      const res = await fetch(`/api/agent/chats/${encodeURIComponent(id)}`, {
        method: "DELETE", credentials: "same-origin", signal: historyRequestRef.current?.signal,
      });
      await checkAgentResponse(res);
      setHistory((items) => items.filter((item) => item.id !== id));
      setHistoryNotice("");
    } catch (err) {
      handleHistoryError(err, "This conversation could not be deleted. Please try again.");
      return;
    } finally {
      setHistoryBusy(false);
    }
    if (conversationId === id) newChat();
  };

  const clearHistory = async () => {
    if (busy) return;
    for (const conversation of history) await deleteChat(conversation.id);
    newChat();
  };

  const send = useCallback(
    async (text) => {
      const message = (text ?? "").trim();
      if (!message || busy) return;

      setDraft("");
      requestAnimationFrame(autosize);
      const botIndex = messages.length + 1;
      setMessages((prev) => [
        ...prev,
        { role: "user", text: message },
        { role: "bot", text: "", matches: [], streaming: true },
      ]);
      setStreaming(true);

      let turnBot = { role: "bot", text: "", matches: [], streaming: true };
      const patchBot = (patch) => {
        turnBot = typeof patch === "function" ? patch(turnBot) : { ...turnBot, ...patch };
        const updatedBot = turnBot;
        setMessages((prev) => {
          const next = [...prev];
          const cur = next[botIndex];
          if (!cur) return prev;
          next[botIndex] = updatedBot;
          return next;
        });
      };

      try {
        const controller = new AbortController();
        requestRef.current = controller;
        const res = await fetch("/api/agent/message", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message, chat_id: sessionId }),
          signal: controller.signal,
        });
        await checkAgentResponse(res);
        if (!res.body) throw new Error("Missing reply stream");

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let acc = "";

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read();
          buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
          const { events, rest } = splitSseFrames(buffer, done);
          buffer = rest;
          for (const part of events) {
            const line = part.trim();
            if (!line.startsWith("data:")) continue;
            let evt;
            try {
              evt = JSON.parse(line.slice(5).trim());
            } catch {
              continue;
            }
            if (evt.type === "delta") {
              acc += evt.text || "";
              const { text: cleanText } = extractQuickReplies(acc);
              patchBot({ text: cleanText, toolStatus: null });
            } else if (evt.type === "tool_status") {
              patchBot({ toolStatus: TOOL_LABELS[evt.tool] || "Working…" });
            } else if (evt.type === "tool_result") {
              const { matches, outOfScope } = extractMatches(evt);
              if (matches.length) patchBot((cur) => ({ ...cur, matches: mergeMatches(cur.matches || [], matches) }));
              if (outOfScope) patchBot((cur) => ({ ...cur, matches: [] }));
            } else if (evt.type === "done") {
              if (evt.chat_id) setSessionId(evt.chat_id);
              const finalText = extractQuickReplies(evt.reply ?? acc);
              patchBot({ text: finalText.text, streaming: false, toolStatus: null });
              if (Array.isArray(evt.matches)) {
                const { matches } = extractMatches({ tool: "search_internal_jobs", data: evt });
                patchBot({ matches: visibleMatches(matches) });
              } else if (evt.finalized_job_id) {
                patchBot((cur) => ({ ...cur, matches: cur.matches.map((m) => ({ ...m, final: m.id === evt.finalized_job_id })) }));
              }
              if (finalText.quickReplies.length) setStarterChips(finalText.quickReplies);
              setConversationId(evt.chat_id || sessionId);
              try {
                await refreshHistory();
                setHistoryNotice("");
              } catch (err) {
                handleHistoryError(err, "History could not be refreshed. Please try again.");
              }
            } else if (evt.type === "error") {
              patchBot({ text: evt.message || "Something went wrong generating a reply.", error: true, streaming: false, toolStatus: null });
            }
          }
          if (done) break;
        }
        patchBot((cur) => (cur.streaming ? {
          ...cur, streaming: false,
          error: true, toolStatus: null, text: `${cur.text}\n\nReply interrupted. Please try again.`,
        } : cur));
      } catch (err) {
        if (err.name === "AbortError") return;
        const notConfigured = err.message === NOT_CONFIGURED;
        if (notConfigured) setUnconfigured(true);
        patchBot({
          text: notConfigured ? NOT_CONFIGURED : "Job Matching Agent is unavailable. Please try again.",
          error: true,
          streaming: false,
          toolStatus: null,
        });
      } finally {
        requestRef.current = null;
        setStreaming(false);
      }
    },
    [messages, sessionId, busy, autosize, refreshHistory, handleHistoryError]
  );

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(draft);
    }
  };

  return (
    <div className="ag-page">
      <div className="ag-eyebrow">Minerva Intelligence &middot; AI Agent</div>
      <h1 className="ag-title">
        Job Matching <em>Agent</em>
      </h1>
      <p className="ag-lede">
        Describe a position in your own words. The agent maps it to the right internal Potentiam job code &mdash; from the 446-role catalog &mdash; explains why, and lets you compare the close calls, so matching is consistent across everyone who uses it.
      </p>

      <div className="ag-export">
        <span className="ag-lbl">Export</span>
        <button type="button" className="ag-exp-btn" disabled title="Export not available yet">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
          </svg>
          <span className="ag-fmt">PDF</span>
        </button>
        <button type="button" className="ag-exp-btn" disabled title="Export not available yet">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 9h18M9 3v18" />
          </svg>
          <span className="ag-fmt">XLS</span>
        </button>
      </div>

      <div className="ag-wrap">
        <section className="ag-history" aria-labelledby="ag-history-title">
          <div className="ag-history-head">
            <h2 id="ag-history-title">Previous requests</h2>
            <button type="button" className="ag-chip" disabled={busy} onClick={newChat}>New chat</button>
            <button type="button" className="ag-chip" disabled={busy || !history.length} onClick={clearHistory}>Clear history</button>
          </div>
          <p className="ag-history-note">{historyLoading ? "Loading history…" : "Saved to your account."}</p>
          {historyNotice && <p className="ag-history-note" role="status">{historyNotice}</p>}
          {history.length ? (
            <ul className="ag-history-list">
              {history.map((conversation) => (
                <li key={conversation.id}>
                  <button type="button" className="ag-history-open" disabled={busy}
                    aria-current={conversationId === conversation.id ? "true" : undefined}
                    onClick={() => reopenChat(conversation)}>
                    <span>{conversation.title}</span>
                    <time dateTime={conversation.updatedAt}>{new Date(conversation.updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</time>
                  </button>
                  <button type="button" className="ag-history-delete" disabled={busy}
                    aria-label={`Delete request: ${conversation.title}`} onClick={() => deleteChat(conversation.id)}>Delete</button>
                </li>
              ))}
            </ul>
          ) : <p className="ag-history-note">No previous requests yet.</p>}
        </section>
        <div className="ag-card">
          <div className="ag-head">
            <div className="ag-icon">
              <RobotIcon />
            </div>
            <div>
              <h2>Minerva Job Matching Agent</h2>
              <div className="ag-sub">AI-powered compensation job matching assistant</div>
            </div>
            <span className="ag-beta">BETA</span>
            {offline && !unconfigured && <span className="ag-offline">SERVICE OFFLINE</span>}
          </div>

          {unconfigured && <p className="ag-config-notice" role="alert">Agent is not configured. Please contact your administrator to enable job matching.</p>}
          <div className="ag-chat" ref={chatRef}>
            {messages.map((msg, i) =>
              msg.role === "user" ? (
                <div className="ag-msg user" key={i}>
                  <div className="ag-av">IS</div>
                  <div className="ag-bubble">{msg.text}</div>
                </div>
              ) : (
                <div key={i}>
                  <BotBubble msg={msg} />
                  {msg.seed && (
                    <div className="ag-chips" style={{ marginLeft: 43 }}>
                      {STARTERS.map((s) => (
                        <button key={s.label} type="button" className="ag-chip" disabled={busy} onClick={() => send(s.q)}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            )}
            {!streaming && starterChips.length > 0 && messages.length > 1 && (
              <div className="ag-chips">
                {starterChips.map((c) => (
                  <button key={c} type="button" className="ag-chip" disabled={busy} onClick={() => send(c)}>
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="ag-composer">
            <textarea
              ref={taRef}
              rows={1}
              placeholder={"Describe a role, e.g. “engineer developing LLM-powered solutions”…"}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                autosize();
              }}
              onKeyDown={onKeyDown}
              disabled={busy}
            />
            <button type="button" className="ag-send" onClick={() => send(draft)} disabled={busy || !draft.trim()} aria-label="Send">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
        <div className="ag-foot">
          <span>Matches are suggestions &mdash; always confirm against the live Potentiam job architecture.</span>
          <span className="ag-mono">model &middot; minerva-match</span>
        </div>
      </div>
    </div>
  );
}
